// Shared service request utilities for webapp and mobile

export type JobProposalDatabaseStatus = 'AWAITING_RESPONSE' | 'PROPOSED' | 'DECLINED' | 'REQUESTED_INFO' | 'USER_INTERESTED' | 'USER_REJECTED';
export type ServiceRequestStatus = 'active' | 'matched' | 'in_progress' | 'completed' | 'cancelled';

export interface BusinessStatusDisplayData {
  text: string;
  hasQuote: boolean;
  hasResponseMessage: boolean;
  statusType: 'waiting' | 'positive' | 'negative' | 'info' | 'user_action';
}

export interface ServiceRequestStatusData {
  text: string;
  statusType: 'in_progress' | 'completed' | 'cancelled' | null;
}

export interface JobProposal {
  status: JobProposalDatabaseStatus;
  quote: number | null;
  detailed_quote?: any | null; // Add detailed_quote support
  response_message: string | null;
}

/**
 * Get business proposal status display data
 * Platform-agnostic logic for determining how to display business proposal status
 */
export const getBusinessStatusData = (proposal: JobProposal): BusinessStatusDisplayData => {
  const hasQuote = proposal.quote !== null && proposal.quote !== undefined;
  const hasDetailedQuote = proposal.detailed_quote !== null && proposal.detailed_quote !== undefined;
  const hasAnyQuote = hasQuote || hasDetailedQuote;
  const hasResponseMessage = proposal.response_message !== null &&
                           proposal.response_message !== undefined &&
                           proposal.response_message.trim() !== '';

  switch (proposal.status) {
    case 'AWAITING_RESPONSE':
      return {
        text: 'Waiting...',
        hasQuote: hasAnyQuote,
        hasResponseMessage,
        statusType: 'waiting'
      };
    case 'PROPOSED':
      return {
        text: 'Proposed',
        hasQuote: hasAnyQuote,
        hasResponseMessage,
        statusType: 'positive'
      };
    case 'DECLINED':
      return {
        text: 'Declined',
        hasQuote: hasAnyQuote,
        hasResponseMessage,
        statusType: 'negative'
      };
    case 'REQUESTED_INFO':
      return {
        text: 'Needs info',
        hasQuote: hasAnyQuote,
        hasResponseMessage,
        statusType: 'info'
      };
    case 'USER_INTERESTED':
      return {
        text: 'You expressed interest',
        hasQuote: hasAnyQuote,
        hasResponseMessage,
        statusType: 'user_action'
      };
    case 'USER_REJECTED':
      return {
        text: 'You declined',
        hasQuote: hasAnyQuote,
        hasResponseMessage,
        statusType: 'user_action'
      };
    default:
      return {
        text: 'Unknown',
        hasQuote: hasAnyQuote,
        hasResponseMessage,
        statusType: 'waiting'
      };
  }
};

/**
 * Get service request status display data
 * Platform-agnostic logic for determining how to display service request status
 */
export const getServiceRequestStatusData = (status: ServiceRequestStatus): ServiceRequestStatusData => {
  switch (status) {
    case 'in_progress':
      return {
        text: 'In Progress',
        statusType: 'in_progress'
      };
    case 'completed':
      return {
        text: 'Completed',
        statusType: 'completed'
      };
    case 'cancelled':
      return {
        text: 'Cancelled',
        statusType: 'cancelled'
      };
    default:
      return {
        text: '',
        statusType: null // Don't show badge for 'active' status
      };
  }
};

/**
 * Format date for service requests
 * Consistent date formatting across platforms
 */
export const formatServiceRequestDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Truncate business name for display
 * Consistent business name truncation logic
 */
export const truncateBusinessName = (name: string | null | undefined, maxLength: number = 20): string => {
  if (!name) return 'Business';

  if (name.length <= maxLength) {
    return name;
  }

  return `${name.substring(0, maxLength)}...`;
};

/**
 * Get attachment count text
 * Consistent attachment count formatting
 */
export const getAttachmentCountText = (count: number): string => {
  return `${count} attachment${count !== 1 ? 's' : ''}`;
};

/**
 * Get proposal count text for more than display limit
 */
export const getMoreProposalsText = (totalCount: number, displayedCount: number): string => {
  const remainingCount = totalCount - displayedCount;
  return `+${remainingCount} more business${remainingCount !== 1 ? 'es' : ''}`;
};

/**
 * Get urgency label for display (internal helper - fallback without translations)
 * Not exported - use getUrgencyDisplay instead
 */
const getUrgencyLabel = (urgency: string): string => {
  const urgencyLabels: Record<string, string> = {
    'urgently': 'Urgently (within 24 hours)',
    'in_the_day': 'Today',
    'tomorrow': 'Tomorrow',
    'in_the_week': 'Within a week',
    'in_the_month': 'Within a month'
  };
  return urgencyLabels[urgency] || urgency.replace(/_/g, ' ');
};

/**
 * Interface for urgency display with label and styling
 */
export interface UrgencyDisplay {
  label: string;
  className: string;
}

/**
 * Get urgency display with label and Tailwind CSS classes
 * Use with translation function for i18n support
 *
 * @param urgency - The urgency value ('urgently', 'in_the_day', etc.)
 * @param translationFn - Optional translation function (e.g., t from react-i18next)
 * @returns Object with label and className for styling
 *
 * @example
 * // With translations (web)
 * const { label, className } = getUrgencyDisplay('urgently', t);
 *
 * // Without translations (fallback)
 * const { label, className } = getUrgencyDisplay('urgently');
 *
 * // Mobile usage
 * const { label } = getUrgencyDisplay('urgently', t);
 */
export const getUrgencyDisplay = (
  urgency: string,
  translationFn?: (key: string) => string
): UrgencyDisplay => {
  // Try translation first, fallback to English labels
  const label = translationFn
    ? (translationFn(`reach_out_modal.urgency.options.${urgency}`) || getUrgencyLabel(urgency))
    : getUrgencyLabel(urgency);

  // Color scheme mapping - consistent across all platforms
  const classNameMap: Record<string, string> = {
    'urgently': 'bg-red-50 text-red-700 border-red-200',
    'in_the_day': 'bg-orange-50 text-orange-700 border-orange-200',
    'tomorrow': 'bg-yellow-50 text-yellow-700 border-yellow-200',
    'in_the_week': 'bg-blue-50 text-blue-700 border-blue-200',
    'in_the_month': 'bg-green-50 text-green-700 border-green-200'
  };

  return {
    label,
    className: classNameMap[urgency] || 'bg-gray-50 text-gray-500 border-gray-200'
  };
};